import React from 'react'
import { Menubar } from 'primereact/menubar'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { Avatar } from 'primereact/avatar'
import { Menu } from 'primereact/menu'

interface ClassicAppBarProps {
    mode: 'light' | 'dark'
    onToggleMode: () => void
    onChangeLang: (lang: 'en' | 'ta' | 'de') => void
}

const ClassicAppBar: React.FC<ClassicAppBarProps> = ({ mode, onToggleMode, onChangeLang }) => {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const [scrolled, setScrolled] = React.useState(false)
    const { authenticated, user, logout, register } = useAuth()
    const menu = React.useRef<Menu>(null)

    React.useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const items = [
        {
            label: t('site.latest'),
            icon: 'pi pi-align-left',
            command: () => { window.location.href = '/news' }
        },
        {
            label: t('site.obituaries'),
            icon: 'pi pi-fw pi-users',
            className: 'menu-item-custom',
            command: () => navigate('/obituaries')
        },
        {
            label: t('site.shops'),
            icon: 'pi pi-fw pi-shopping-bag',
            className: 'menu-item-custom',
            command: () => navigate('/shops')
        },
        {
            label: t('site.cinema'),
            icon: 'pi pi-fw pi-video',
            className: 'menu-item-custom',
            command: () => navigate('/cinema')
        },
        {
            label: t('site.bakthi'),
            icon: 'pi pi-fw pi-heart',
            className: 'menu-item-custom',
            command: () => navigate('/bakthi')
        }
    ]

    const userMenuItems = [
        {
            label: 'Dashboard',
            icon: 'pi pi-home',
            command: () => navigate('/admin')
        },
        {
            separator: true
        },
        {
            label: 'Logout',
            icon: 'pi pi-power-off',
            command: () => logout()
        }
    ]

    const start = (
        <div
            className="flex align-items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
            style={{
                transition: 'all 0.3s ease',
            }}
        >
            <div
                className="flex align-items-center justify-content-center border-circle"
                style={{
                    width: '42px',
                    height: '42px',
                    background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                    boxShadow: '0 4px 15px rgba(211, 84, 0, 0.4)',
                    animation: 'pulse 3s ease-in-out infinite'
                }}
            >
                <span className="text-white font-bold text-xl" style={{ fontFamily: 'serif' }}>யா</span>
            </div>
            <div>
                <div
                    className="font-bold text-2xl"
                    style={{
                        background: 'linear-gradient(135deg, #1a252f 0%, #d35400 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontFamily: 'serif',
                        letterSpacing: '0.5px'
                    }}
                >
                    YarlHub
                </div>
                <div className="text-xs text-500" style={{ marginTop: '-4px' }}>
                    Tamil Community Portal
                </div>
            </div>
        </div>
    )

    const languageOptions = [
        { label: 'English', value: 'en', flag: '🇬🇧' },
        { label: 'தமிழ்', value: 'ta', flag: '🇱🇰' },
        { label: 'Deutsch', value: 'de', flag: '🇩🇪' }
    ]

    const getCurrentLanguageValue = () => {
        return i18n.language || 'en'
    }

    const selectedLanguageTemplate = (option: any) => {
        if (option) {
            return (
                <div className="flex align-items-center gap-2">
                    <span style={{ fontSize: '1.2rem', marginLeft: '0.5rem' }}>{option.flag}</span>
                    <span className="font-semibold">{option.label}</span>
                </div>
            )
        }
        return <span>Language</span>
    }

    const languageOptionTemplate = (option: any) => {
        return (
            <div className="flex align-items-center gap-2 p-2">
                <span style={{ fontSize: '1.2rem' }}>{option.flag}</span>
                <span className="font-medium">{option.label}</span>
            </div>
        )
    }

    const end = (
        <div className="flex align-items-center gap-2">
            <Dropdown
                value={getCurrentLanguageValue()}
                options={languageOptions}
                onChange={(e) => onChangeLang(e.value)}
                optionLabel="label"
                valueTemplate={selectedLanguageTemplate}
                itemTemplate={languageOptionTemplate}
                placeholder="Language"
                className="hidden md:flex"
                style={{
                    width: '150px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(211, 84, 0, 0.4)',
                    borderRadius: '8px'
                }}
                panelStyle={{
                    background: 'rgba(26, 37, 47, 0.98)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(211, 84, 0, 0.3)'
                }}
            />
            <Button
                icon={mode === 'dark' ? 'pi pi-sun' : 'pi pi-moon'}
                className="p-button-rounded p-button-text"
                style={{
                    color: mode === 'dark' ? '#fbbf24' : '#6366f1',
                    transition: 'all 0.3s ease',
                    marginLeft: '0.5rem'
                }}
                onClick={onToggleMode}
                tooltip={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
                tooltipOptions={{ position: 'bottom' }}
            />

            <div className="w-1rem"></div>

            {!authenticated ? (
                <>
                    <Button
                        label={t('site.login') || 'Login'}
                        className="p-button-text font-bold mr-3 px-3 py-2"
                        style={{ color: 'rgba(255,255,255,0.9)' }}
                        onClick={() => {
                            // login() requires credentials (native auth) – route to the login page instead
                            const current = `${window.location.pathname}${window.location.search}`
                            const redirect = current && current !== '/' && !current.startsWith('/login')
                                ? `?redirect=${encodeURIComponent(current)}`
                                : ''
                            navigate(`/login${redirect}`)
                        }}
                    />
                    <Button
                        label={t('site.register') || 'Register'}
                        className="font-bold border-round-lg px-4 py-2"
                        style={{
                            background: '#d35400',
                            borderColor: '#d35400',
                            color: 'white'
                        }}
                        onClick={() => register()}
                    />
                </>
            ) : (
                <>
                    <Menu model={userMenuItems} popup ref={menu} />
                    <div
                        className="flex align-items-center gap-2 cursor-pointer hover:surface-hover p-1 border-round transition-duration-200"
                        onClick={(e) => menu.current?.toggle(e)}
                    >
                        <Avatar
                            label={user?.username?.charAt(0).toUpperCase() || 'U'}
                            shape="circle"
                            style={{ backgroundColor: '#d35400', color: '#ffffff' }}
                        />
                        <span className="text-white font-medium hidden md:block">
                            {user?.username}
                        </span>
                        <i className="pi pi-angle-down text-white-alpha-70"></i>
                    </div>
                </>
            )}
        </div>
    )

    return (
        <>
            <style>
                {`
                    .modern-menubar {
                        background: linear-gradient(135deg, 
                            rgba(26, 37, 47, 0.98) 0%, 
                            rgba(44, 62, 80, 0.98) 50%,
                            rgba(26, 37, 47, 0.98) 100%
                        ) !important;
                        backdrop-filter: blur(10px);
                        border: none !important;
                        border-radius: 0 !important;
                        box-shadow: ${scrolled
                        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                        : '0 2px 8px rgba(0, 0, 0, 0.1)'
                    };
                        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                        border-bottom: 3px solid transparent !important;
                        border-image: linear-gradient(90deg, #d35400 0%, #e67e22 50%, #d35400 100%) 1 !important;
                        padding: 0.5rem 2rem !important;
                    }

                    .modern-menubar .p-menubar-start {
                        margin-right: 3rem;
                    }

                    .modern-menubar .p-menubar-root-list {
                        gap: 0.5rem;
                    }

                    .modern-menubar .p-menuitem-link {
                        color: rgba(255, 255, 255, 0.9) !important;
                        font-weight: 500;
                        font-size: 1.2rem !important;
                        padding: 0.75rem 1.25rem !important;
                        border-radius: 8px;
                        transition: all 0.3s ease;
                        position: relative;
                        overflow: hidden;
                    }

                    .modern-menubar .p-menuitem-link::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background: linear-gradient(135deg, rgba(211, 84, 0, 0.2), rgba(230, 126, 34, 0.2));
                        opacity: 0;
                        transition: opacity 0.3s ease;
                        z-index: -1;
                    }

                    .modern-menubar .p-menuitem-link:hover {
                        color: #ffffff !important;
                        background: rgba(211, 84, 0, 0.2) !important;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(211, 84, 0, 0.3);
                    }

                    .modern-menubar .p-menuitem-link:hover::before {
                        opacity: 1;
                    }

                    .modern-menubar .p-menuitem-link .p-menuitem-icon {
                        color: #d35400 !important;
                        margin-right: 0.5rem;
                        font-size: 1.3rem !important;
                        transition: all 0.3s ease;
                    }

                    .modern-menubar .p-menuitem-link:hover .p-menuitem-icon {
                        color: #e67e22 !important;
                        transform: scale(1.1);
                    }

                    .modern-menubar .p-menuitem-link:active {
                        transform: translateY(0);
                    }

                    @keyframes pulse {
                        0%, 100% {
                            box-shadow: 0 4px 15px rgba(211, 84, 0, 0.4);
                        }
                        50% {
                            box-shadow: 0 4px 25px rgba(211, 84, 0, 0.6);
                        }
                    }

                    @keyframes glow {
                        0%, 100% {
                            filter: brightness(1);
                        }
                        50% {
                            filter: brightness(1.2);
                        }
                    }
                `}
            </style>
            <div
                className="sticky top-0 z-5"
                style={{
                    transition: 'all 0.3s ease',
                }}
            >
                <Menubar
                    model={items}
                    start={start}
                    end={end}
                    className="modern-menubar"
                />
            </div>
        </>
    )
}

export default ClassicAppBar
